export interface DayPassOption {
  id: string;
  name: string;
  fee: number; // cents
  /** Pass ids this pass subsumes (a weekend pass covers the single days). */
  covers?: string[];
  /** Render the best-value badge for this pass. */
  highlight?: boolean;
}

export interface PassEntry {
  dayPassId: string;
  attendeeName: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_PASSES = 20;

export function computeSubtotalCents(entries: PassEntry[], options: DayPassOption[]): number {
  const feeById = new Map(options.map((o) => [o.id, o.fee]));
  return entries.reduce((sum, e) => sum + (feeById.get(e.dayPassId) ?? 0), 0);
}

export function validateCart(
  entries: PassEntry[],
  options: DayPassOption[],
  email: string,
): { ok: true } | { ok: false; error: string } {
  if (entries.length === 0) return { ok: false, error: 'Add at least one day pass.' };
  if (entries.length > MAX_PASSES) return { ok: false, error: 'You can buy at most 20 passes per order.' };
  if (!EMAIL_RE.test(email.trim())) return { ok: false, error: 'Enter a valid email address.' };

  const ids = new Set(options.map((o) => o.id));
  for (const e of entries) {
    if (!ids.has(e.dayPassId)) return { ok: false, error: 'One or more selected passes are unavailable.' };
    if (e.attendeeName.trim() === '') return { ok: false, error: 'Enter a name for every pass.' };
  }
  return { ok: true };
}

/**
 * What a bundle pass saves against buying each of the days it covers, in cents.
 * 0 when the pass covers nothing, or when it is not actually cheaper.
 *
 * Computed rather than written into copy, so repricing a single day in site_settings
 * can never leave a badge claiming a saving that no longer exists. A covered id with
 * no matching pass contributes 0, which understates the saving — deliberately
 * conservative: better a quiet badge than an overstated one. A pass never covers
 * itself, and a repeated id in `covers` is only counted once — both `covers` and
 * `options` are admin-editable data, so this can't be assumed away.
 */
export function bundleSavingsCents(option: DayPassOption, options: DayPassOption[]): number {
  const covered = [...new Set(option.covers ?? [])].filter((id) => id !== option.id);
  if (covered.length === 0) return 0;
  const feeById = new Map(options.map((o) => [o.id, o.fee]));
  const partsTotal = covered.reduce((sum, id) => sum + (feeById.get(id) ?? 0), 0);
  return Math.max(0, partsTotal - option.fee);
}
